import 'react-circular-progressbar/dist/styles.css';
import React, { Component } from 'react';
import { Button } from 'antd';
import { CircularProgressbar } from 'react-circular-progressbar';
import isEqual from 'lodash/isEqual';
import { TwinConfig } from '@ynomia/core/dist/blueprint';
import { ACCESS_TOKEN_STORAGE_KEY } from '../../../config/constants';
import { TwinData } from '../../../config/types';
import { calculateLoadingPercentage } from '../../../utils/modelViewing';
import { notification } from '../../../antdProvider';
import styles from './styles.module.less';
import { getCurrentRegion } from '../../../config';

type HoverData = {
  twinId: string | undefined,
};

interface Props {
  source: string
  modelCode: string
  twinData: TwinData | undefined
  config: string
  loadModels: Array<string>
  project: string
  tenant: string,
  isTwinLoading: boolean
  selectedTwinIds?: Array<string>
  forgeConfig: TwinConfig['forge'],
  /**
   * Update this date every time we want the twin to update.
   * We are handling this manually to prevent an infinite loop from happening
   * where the twin sends different data to Web at the same time as Web sends
   * data to the twin.
   * Don't update this if the update is triggered by the twin.
   * ie. If the user selects from the twin and web's selection gets update.
   */
  lastExternalUpdate?: Date
  /**
   * Bump this to reload the underlying twin iframe (e.g. after a mapping
   * finishes) so the twin re-fetches the freshly-saved mapping from the backend.
   */
  requestModelReload?: number
  mappingTwinIds?: Array<string>
  /**
   * Layer paths (in getLayerPathFromLayerID format) the twin should isolate to.
   * Passed through as the third item of the `updateColorsByTwinID` message.
   */
  isolatedTwinIds?: Array<string>
  getHoverTooltipData?: (data: HoverData) => any;
  onTwinReady?: (boolean, coordinates: { x: number, y: number, z: number }) => void
  onTwinLoading?: (number) => void
  onTwinCancel?: () => void
  onSelectTwin?: (
    twinIDsInTwin: Array<string>,
    twinIDNotInTwin: Array<string>,
    isMultiSelect: boolean
  ) => void
  onHoverTwin?: (ev: HoverData) => void
  onCameraChangedWhileNothingSelected?: (cameraState: any) => void
  presenterIdle?: boolean
  disableSelection?: boolean
  disableShiftSelection?: boolean
}

interface State {
  modelByKeys: { [id: string]: { id: string, loading: number } };
  // This represents the twinIDs that are currently selected in the model (iFrame)
  // To be used to detect if the model is still syncing with Web.
  modelSelectedTwinIds: Array<string>;
  // Some selected twinIds are not present in the model
  selectedTwinIdsNotInTwin: Array<string>;
}

export default class ModelViewer extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      modelByKeys: {},
      modelSelectedTwinIds: [],
      selectedTwinIdsNotInTwin: [],
    };

    this.viewerRef = React.createRef();
    this.isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  }

  componentDidMount() {
    window.addEventListener('message', this.onMessage, false);
    // Otherwise only set on update, which a viewer mounted with its data may never get.
    this.latestTwinData = this.props.twinData;
    this.latestSelectedTwinIds = this.props.selectedTwinIds;
    const { onTwinLoading } = this.props;
    if (onTwinLoading) {
      onTwinLoading(0);
    }
    if ('Notification' in window && !this.isMobile) {
      Notification.requestPermission();
    }
  }

  componentDidUpdate(prevProps) {
    const {
      twinData,
      loadModels,
      onTwinLoading,
      selectedTwinIds,
      presenterIdle,
    } = this.props;

    this.latestSelectedTwinIds = selectedTwinIds;
    this.latestTwinData = twinData;

    if (prevProps.presenterIdle !== presenterIdle) {
      if (presenterIdle) {
        this.postMessage(['hideUI']);
      } else {
        this.postMessage(['showUI']);
      }
    }

    const isolatedTwinIdsChanged = !isEqual(prevProps.isolatedTwinIds, this.props.isolatedTwinIds);

    if (!isEqual(prevProps.twinData?.hexColorsKeyedByTwinId, twinData?.hexColorsKeyedByTwinId)
      || !isEqual(prevProps.selectedTwinIds, selectedTwinIds)
      || prevProps.lastExternalUpdate?.getTime() !== this.props.lastExternalUpdate?.getTime()
      || isolatedTwinIdsChanged
    ) {
      const { selectedTwinIdsNotInTwin, selectedTwinIdsInTwin } = this.getSelectedTwinIds();
      this.setState({ selectedTwinIdsNotInTwin });
      // On isolation changes, force both the colour message (colours may be
      // unchanged) and the selection (newly unhidden elements need reapplying).
      this.postMessageWithData(
        twinData,
        selectedTwinIdsInTwin,
        isolatedTwinIdsChanged,
      );
    }

    if ((loadModels.toString() !== prevProps.loadModels.toString())) {
      if (onTwinLoading) {
        onTwinLoading(0);
      }
    }

    if (
      this.props.requestModelReload !== undefined
      && this.props.requestModelReload !== prevProps.requestModelReload
    ) {
      this.reloadModel();
    }

    // When the twin loads before the assets load,
    // we want to request fit to view once the data rolls in
    if (prevProps.twinData?.lastFetched === null && twinData?.lastFetched !== null) {
      this.postMessage(['requestFitToView']);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.onMessage, false);
    clearInterval(this.startupInterval);
  }

  viewerRef: React.RefObject<HTMLIFrameElement>;

  isMobile: boolean;

  startupInterval?: ReturnType<typeof setInterval>;

  latestSelectedTwinIds?: Array<string>;

  latestTwinData?: TwinData;

  lastTwinInteraction: Date = new Date();

  shouldTriggerOnCameraChangedWhileNothingSelected = true;

  outOfSyncTimeoutId:ReturnType<typeof setTimeout> | null = null;

  // eslint-disable-next-line react/no-unused-class-component-methods
  requestFitToView = (onlyIfIdle = false) => {
    const { lastExternalUpdate } = this.props;

    if (
      onlyIfIdle
    && !(lastExternalUpdate && this.getUpdatePriority() === 'web')
    ) {
      return;
    }

    this.postMessage(['requestFitToView']);
  };

  // eslint-disable-next-line react/no-unused-class-component-methods
  setView = (cameraState) => {
    this.postMessage(['setView', cameraState]);
  };

  /**
   * Reload the twin iframe. There is no postMessage to reload a model, and the
   * iframe is cross-origin (so `contentWindow.location.reload()` throws), so we
   * re-assign the same `src` which forces the browser to reload it. Kick the
   * loading state back to 0 so the loading overlay shows while it reloads.
   */
  reloadModel = () => {
    const iframe = this.viewerRef.current;
    if (!iframe) return;
    this.props.onTwinLoading?.(0);
    // eslint-disable-next-line no-self-assign
    iframe.src = iframe.src;
  };

  postMessage = (message) => {
    this.viewerRef?.current?.contentWindow?.postMessage(message, '*');
  };

  getSelectedTwinIds = () => {
    const { mappingTwinIds = [] } = this.props;
    const selectedTwinIdsNotInTwin: Array<string> = [];
    const selectedTwinIdsInTwin: Array<string> = [];
    this.latestSelectedTwinIds?.forEach((twinID) => {
      if (!mappingTwinIds.includes(twinID)) {
        selectedTwinIdsNotInTwin.push(twinID);
      } else {
        selectedTwinIdsInTwin.push(twinID);
      }
    });
    return {
      selectedTwinIdsNotInTwin,
      selectedTwinIdsInTwin,
    };
  };

  previousHexColorsKeyedByTwinId?: TwinData['hexColorsKeyedByTwinId'] = {};

  postMessageWithData = (
    twinData: TwinData | undefined,
    selectedTwinIds: Array<string>,
    forceColorUpdate = false,
  ) => {
    const {
      modelCode, config, forgeConfig, isolatedTwinIds,
    } = this.props;
    this.postMessage(['setColorConfig', config]);

    if (
      forceColorUpdate
      || !isEqual(this.previousHexColorsKeyedByTwinId, twinData?.hexColorsKeyedByTwinId)
    ) {
      this.postMessage([
        'updateColorsByTwinID',
        [twinData?.hexColorsKeyedByTwinId || {}, modelCode, isolatedTwinIds],
      ]);
    }
    if (this.getUpdatePriority() === 'web') {
      this.postMessage(['setSelectedTwinIds', selectedTwinIds]);
    }
    this.previousHexColorsKeyedByTwinId = twinData?.hexColorsKeyedByTwinId;

    if ((forgeConfig as any)?.showAll) {
      this.postMessage(['showAll']);
    }
  };

  modelSelectedTwinIdsInSync = () => {
    const { mappingTwinIds = [] } = this.props;
    const { selectedTwinIdsInTwin } = this.getSelectedTwinIds();
    const { modelSelectedTwinIds, selectedTwinIdsNotInTwin } = this.state;

    // Don't care about sync if no "selectedTwinIds" prop is provided
    if (!this.latestSelectedTwinIds) return true;

    // Only compare twinIds that are actually present in the twin. Ids that
    // aren't part of the loaded model can never be reflected in the model's
    // selection (e.g. viewing an asset whose geometry isn't in the twin — the
    // asset still has a status colour, but no geometry to select), so treating
    // them as out-of-sync would wrongly trip the 2s failsafe reset and close
    // the asset details pane.
    const a = Array.from(new Set(modelSelectedTwinIds))
      .concat(selectedTwinIdsNotInTwin)
      .filter(twinId => mappingTwinIds.includes(twinId))
      .sort();
    const b = Array.from(new Set(selectedTwinIdsInTwin)).sort();
    return isEqual(a, b);
  };

  getLoadingPercentage = () => {
    const { modelByKeys } = this.state;
    const { loadModels } = this.props;
    const calculated = calculateLoadingPercentage(modelByKeys, loadModels);
    return Math.min(calculated, 99);
  };

  onMessage = (event) => {
    const functionName = event.data[0];
    if (this.registeredFunctions[functionName]) {
      const args = event.data.slice(1);
      this.registeredFunctions[functionName](...(args ?? []));
    }
  };

  registeredFunctions = {
    onTwinProgress: (id: string, percentage: number) => {
      const { onTwinLoading } = this.props;
      const { modelByKeys } = this.state;
      const newModelByKeys = {
        ...modelByKeys,
        [id]: {
          id,
          loading: percentage,
        },
      };
      this.setState({
        modelByKeys: newModelByKeys,
      }, () => {
        if (onTwinLoading) {
          onTwinLoading(this.getLoadingPercentage());
        }
      });
    },
    onTwinReady: (coordinates: { x: number, y: number, z: number }) => {
      const {
        onTwinReady,
        onTwinLoading,
        isTwinLoading,
        forgeConfig,
      } = this.props;
      onTwinReady?.(true, coordinates);
      onTwinLoading?.(100);

      // The twin may self reload for various reasons.
      // We only want to send notifications when the user deliberately loads the twin
      // which is the only time when isTwinLoading starts off as true.
      if (isTwinLoading) {
        if (!document.hasFocus() && !this.isMobile && ('Notification' in window)) {
          // eslint-disable-next-line no-new
          new Notification('Digital twin is ready for viewing', {
            icon: '/src/favicon.png',
            tag: 'digital_twin_ready',
          });
        }

        notification.success({
          key: 'digital_twin_success',
          message: 'Digital twin is ready for viewing',
        });
      }

      // When we receive this, it means the twin thinks it's fully loaded
      // (or has just reloaded) so it needs a full repaint. Force the colour
      // update: `previousHexColorsKeyedByTwinId` may still equal the current
      // colours from before a reload, which would otherwise skip the paint and
      // leave the freshly-loaded twin uncoloured.
      const { twinData, selectedTwinIds } = this.props;
      this.postMessageWithData(twinData, selectedTwinIds || [], true);
      if ((forgeConfig as any)?.setGhosting) {
        this.postMessage(['setGhosting', true]);
      }

      // Unfortunately, sometimes this event fires too early.
      // So we repaint it every second until we detect user activity.
      // Selection is optional: screens that don't pass `selectedTwinIds` still need repainting.
      let maxRepaintCount = 3;
      // A reload fires this again; don't leave the previous loop running alongside.
      clearInterval(this.startupInterval);
      this.startupInterval = setInterval(() => {
        if (this.latestTwinData) {
          const { selectedTwinIdsInTwin } = this.getSelectedTwinIds();
          this.postMessageWithData(this.latestTwinData, selectedTwinIdsInTwin, true);
        }
        // Counted either way, so it can't tick forever while there's nothing to paint.
        maxRepaintCount -= 1;
        if (maxRepaintCount <= 0) clearInterval(this.startupInterval);
      }, 1000);
    },
    getSelectedTwinIds: (rawTwinIds: string) => {
      const { disableSelection, disableShiftSelection, onSelectTwin } = this.props;
      const { selectedTwinIdsNotInTwin } = this.state;
      const {
        twinIds,
        isShiftHeld,
      } = JSON.parse(rawTwinIds) as {
        twinIds: Array<string>,
        boxSelectionActive: boolean,
        isDoubleClick: boolean,
        isShiftHeld: boolean,
      };
      if (isShiftHeld && disableShiftSelection) return;
      if (!isShiftHeld && disableSelection) return;
      this.shouldTriggerOnCameraChangedWhileNothingSelected = twinIds.length === 0;

      this.setState(() => ({ modelSelectedTwinIds: twinIds }));
      if (this.getUpdatePriority() === 'twin') {
        onSelectTwin?.(
          twinIds,
          selectedTwinIdsNotInTwin,
          isShiftHeld,
        );
      }
    },
    elementHovered: (hoverData: HoverData) => {
      this.props.onHoverTwin?.(hoverData);
      if (!hoverData) {
        this.postMessage(['updateHoverTooltip', undefined]);
        return;
      }
      const tooltipData = this.props.getHoverTooltipData?.(hoverData);
      this.postMessage(['updateHoverTooltip', tooltipData]);
    },
    onInteract: () => {
      this.lastTwinInteraction = new Date();
    },
    onCameraChanged: (state) => {
      if (this.shouldTriggerOnCameraChangedWhileNothingSelected) {
        this.props.onCameraChangedWhileNothingSelected?.(state.cameraState);
      }
    },
  };

  /*
   * The twin does not distinguish programmatically made selection
   * changes and user twin interactions, meaning that an alternate
   * method needs to be used to determine which user is interacting.
   * In this implementation, the user is considered to be interacting
   * with the twin if their mouse had entered the twin sooner than
   * the last confirmed manual update.
   */
  getUpdatePriority = () => {
    const { lastExternalUpdate } = this.props;
    if (lastExternalUpdate && (lastExternalUpdate > this.lastTwinInteraction)) {
      return 'web';
    }
    return 'twin';
  };

  /**
   * This is a failsafe for when the selection on the twin is
   * out of sync with the selections on WebHQ for over 5 seconds.
   * We clear selections on both sides as a safe "reset".
   */
  onOutOfSyncTimeout() {
    const { onSelectTwin } = this.props;
    this.postMessage(['setSelectedTwinIds', []]);
    onSelectTwin?.([], [], false);
  }

  startOutOfSyncTimer() {
    this.clearOutOfSyncTimer();

    this.outOfSyncTimeoutId = setTimeout(() => {
      this.onOutOfSyncTimeout();
    }, 2000);
  }

  clearOutOfSyncTimer() {
    if (this.outOfSyncTimeoutId) {
      clearTimeout(this.outOfSyncTimeoutId);
      this.outOfSyncTimeoutId = null;
    }
  }

  render() {
    const {
      source, project, tenant, loadModels, isTwinLoading, presenterIdle, forgeConfig,
    } = this.props;
    const accessToken = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
    const forgeConfigStr = encodeURIComponent(forgeConfig ? JSON.stringify(forgeConfig) : '{}');
    const urlParams = `\
accessToken=${accessToken}\
&project=${project}&tenant=${tenant}\
&loadModels=${loadModels}\
&forgeConfig=${forgeConfigStr}\
&env=${getCurrentRegion()}`;
    const twinIdsInSync = this.modelSelectedTwinIdsInSync();

    if (twinIdsInSync) {
      this.clearOutOfSyncTimer();
    } else {
      this.startOutOfSyncTimer();
    }

    return (
      <>
        <div
          className={styles.syncingContainer}
          style={
            twinIdsInSync
              ? { opacity: 0, pointerEvents: 'none' }
              : { opacity: 0.5 }
          }
        />
        {isTwinLoading
          && (
            <div className={styles.loadingContainer}>
              <div className={styles.loadingText}>
                <div>Digital Twin Loading...</div>
                <div>We will notify you when ready</div>
              </div>
              <div style={{ width: 90, height: 90 }}>
                <CircularProgressbar
                  value={this.getLoadingPercentage()}
                  text={`${this.getLoadingPercentage()}%`}
                />
              </div>
              <Button
                style={{ marginTop: 50 }}
                onClick={this.props.onTwinCancel}
              >
                Cancel
              </Button>
            </div>
          )}
        <iframe
          onMouseLeave={() => {
            this.props.onHoverTwin?.({
              twinId: undefined,
            });
            this.postMessage(['updateHoverTooltip', undefined]);
          }}
          ref={this.viewerRef}
          src={`${source}/?${urlParams}`}
          allowFullScreen
          frameBorder="0"
          title="Twin"
          style={{
            minWidth: '300px',
            width: '100%',
            height: '100%',
            flex: 1,
            margin: '0 auto',
            position: 'absolute',
            borderRadius: 4,
            visibility: isTwinLoading ? 'hidden' : 'visible',
            pointerEvents: presenterIdle ? 'none' : 'auto',
          }}
          onLoad={() => {
            this.postMessage(['updateToolbar', '']);
          }}
        />
      </>
    );
  }
}
